import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

import { dashboardActions } from "../redux/dashboard/dashboard.slice";
import { formActions } from "../redux/forms/form.slice";
import { settingsActions } from "../redux/settings/settings.slice";
import store from "../redux/store";
import { thresholdActions } from "../redux/threshold/threshold.slice";
import { userActions } from "../redux/user/user.slice";
import { moduleActions } from "../redux/module/module.slice";
import { entryActions } from "../redux/entry/entry.slice";
import { appActions } from "../redux/app/app.slice";
import { orgChatActions } from "../redux/chat/orgChat.slice";
import { tagActions } from "../redux/tag/tag.slice";
import { getTokenUserId } from "../redux/store-utils";
import { UserRoles } from "../model/user/types/UserRoles";

const APP_START_REQUESTS_GAP_MS = 150;

export async function onAppStart(cb?: () => void) {
  const dispatch = store.store.dispatch;

  try {
    const state = store.store.getState();
    // Fall back to the token's own claim: sessionBoot drops persisted state
    // that belonged to a different account, so a tab can legitimately hold a
    // valid token with no identity in state yet. Deriving it from the token
    // keeps the token authoritative instead of bouncing the user to /login.
    const userId = state.user.loggedInUserId || getTokenUserId();
    const loggedInUser = state.user.loggedInUser;

    if (userId) {
      // await dispatch(userActions.refreshToken()).unwrap();
      const user = await dispatch(
        userActions.getLoggedInUser({ userId }),
      ).unwrap();
      dispatch(appActions.setChatWidgetVisible(true));

      const hasOrgRole =
        user?.roles?.includes(UserRoles.MEMBER) ||
        user?.roles?.includes(UserRoles.ORGANIZATION);

      // Most needed first. Spaced out rather than sent all at once, and
      // dropped if the user has logged out or switched account meanwhile
      const requests = [
        () => dispatch(settingsActions.getSettings({ userId })),
        () => dispatch(dashboardActions.getDashboardStats({ userId })),
        () => dispatch(entryActions.getUserTableSettings({ userId })),
        () => dispatch(thresholdActions.getThresholds({ userId })),
        () => dispatch(formActions.getForms({ userId })),
        () => dispatch(moduleActions.getAllModuleDefinitions({ userId })),
        () => dispatch(tagActions.getTags()),
        () => dispatch(userActions.psaGetLastUnread({ userId })),
        ...(hasOrgRole
          ? [() => dispatch(orgChatActions.getUnreadCount())]
          : []),
      ];

      requests.forEach((request, i) =>
        setTimeout(() => {
          if (store.store.getState().user.loggedInUserId === userId) {
            request();
          }
        }, i * APP_START_REQUESTS_GAP_MS),
      );

      Sentry.setUser({
        id: userId,
        email: loggedInUser?.email,
        username: loggedInUser?.name,
      });
    }

    cb?.();
  } catch (e: any) {
    const error = e as AxiosError;

    if (error.response?.status === 403) {
      await dispatch(userActions.logoutAndClearPersistence());
    }
  }
}
